import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, CheckCircle, Target, AlertTriangle, BookOpen, Info } from 'lucide-react';

const BACASSPSlideRenderer = ({ slide, theme }) => {
  switch (slide.type) {
    case 'title':
      return (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', color: 'white', fontSize: '1.5rem', fontWeight: 'bold' }}>{slide.icon || '📚'}</div>
          <h1 style={{ fontSize: '1.8rem', color: theme.primary, marginBottom: '0.8rem' }}>{slide.title}</h1>
          {slide.subtitle && <p style={{ fontSize: '1rem', color: '#64748B' }}>{slide.subtitle}</p>}
        </div>
      );
    case 'content':
      return (
        <div style={{ padding: '0.5rem' }}>
          <h2 style={{ fontSize: '1.4rem', color: theme.primary, marginBottom: '1.2rem' }}>{slide.title}</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {slide.content.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.7rem', padding: '0.7rem', background: slide.highlight ? `${theme.primary}08` : '#F8FAFC', borderRadius: '8px', borderLeft: `3px solid ${theme.primary}` }}>
                <CheckCircle size={16} color={theme.primary} style={{ marginTop: '2px', flexShrink: 0 }} />
                <span style={{ fontSize: '0.9rem', color: '#1E293B', lineHeight: '1.4' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );
    case 'definition':
      return (
        <div style={{ padding: '0.5rem' }}>
          <h2 style={{ fontSize: '1.4rem', color: theme.primary, marginBottom: '1.2rem' }}>{slide.title}</h2>
          <div style={{ background: `linear-gradient(135deg, ${theme.primary}10, ${theme.secondary}10)`, padding: '1.5rem', borderRadius: '12px', border: `1px solid ${theme.primary}30`, marginBottom: '1rem' }}>
            <p style={{ fontSize: '1rem', fontStyle: 'italic', color: theme.primary, lineHeight: '1.5' }}>{slide.definition}</p>
          </div>
          {slide.details && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {slide.details.map((d, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.5rem' }}>
                  <Info size={15} color={theme.secondary} style={{ marginTop: '2px', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.9rem', color: '#475569' }}>{d}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    case 'grid':
      return (
        <div style={{ padding: '0.5rem' }}>
          <h2 style={{ fontSize: '1.4rem', color: theme.primary, marginBottom: '1.2rem' }}>{slide.title}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${slide.cols || 2}, 1fr)`, gap: '0.8rem' }}>
            {slide.items.map((item, i) => (
              <div key={i} style={{ padding: '1rem', background: 'white', borderRadius: '10px', border: `1px solid ${theme.primary}25`, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <h3 style={{ fontSize: '0.95rem', color: theme.primary, marginBottom: '0.4rem' }}>{item.title}</h3>
                <p style={{ fontSize: '0.85rem', color: '#64748B', lineHeight: '1.4' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      );
    case 'list':
      return (
        <div style={{ padding: '0.5rem' }}>
          <h2 style={{ fontSize: '1.4rem', color: theme.primary, marginBottom: '1.2rem' }}>{slide.title}</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {slide.items.map((item, i) => (
              <div key={i} style={{ padding: '0.8rem', background: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                <h4 style={{ color: theme.primary, marginBottom: '0.2rem', fontSize: '0.95rem' }}>{item.title}</h4>
                <p style={{ fontSize: '0.85rem', color: '#64748B', lineHeight: '1.3' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      );
    case 'warning':
      return (
        <div style={{ padding: '0.5rem' }}>
          <h2 style={{ fontSize: '1.4rem', color: theme.primary, marginBottom: '1.2rem' }}>{slide.title}</h2>
          <div style={{ background: '#FEF3C7', padding: '1.2rem', borderRadius: '10px', border: '1px solid #FCD34D', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <AlertTriangle size={18} color="#D97706" />
              <span style={{ fontWeight: '600', color: '#92400E' }}>Point de vigilance VAE</span>
            </div>
            <p style={{ fontSize: '0.9rem', color: '#78350F', lineHeight: '1.4' }}>{slide.warning}</p>
          </div>
          {slide.content && slide.content.map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.5rem' }}>
              <CheckCircle size={15} color={theme.primary} style={{ marginTop: '2px', flexShrink: 0 }} />
              <span style={{ fontSize: '0.9rem', color: '#475569' }}>{item}</span>
            </div>
          ))}
        </div>
      );
    case 'table':
      return (
        <div style={{ padding: '0.5rem' }}>
          <h2 style={{ fontSize: '1.4rem', color: theme.primary, marginBottom: '1.2rem' }}>{slide.title}</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr>{slide.headers.map((h, i) => <th key={i} style={{ padding: '0.6rem', background: theme.primary, color: 'white', textAlign: 'left' }}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {slide.rows.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#F8FAFC' : 'white' }}>
                    {row.map((cell, j) => <td key={j} style={{ padding: '0.6rem', borderBottom: '1px solid #E2E8F0' }}>{cell}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    case 'summary':
      return (
        <div style={{ padding: '0.5rem' }}>
          <h2 style={{ fontSize: '1.4rem', color: theme.primary, marginBottom: '1.2rem' }}>{slide.title}</h2>
          <div style={{ background: `linear-gradient(135deg, ${theme.primary}08, ${theme.secondary}08)`, padding: '1.5rem', borderRadius: '12px' }}>
            {slide.items.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.7rem', marginBottom: '0.7rem' }}>
                <Target size={16} color={theme.primary} style={{ marginTop: '2px', flexShrink: 0 }} />
                <span style={{ fontSize: '0.9rem', color: '#1E293B', lineHeight: '1.4' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );
    case 'process':
      return (
        <div style={{ padding: '0.5rem' }}>
          <h2 style={{ fontSize: '1.4rem', color: theme.primary, marginBottom: '1.2rem' }}>{slide.title}</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {slide.steps.map((step, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.8rem', padding: '0.7rem', background: 'white', borderRadius: '8px', border: `1px solid ${theme.primary}20` }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: theme.primary, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 'bold', flexShrink: 0 }}>{i + 1}</div>
                <div>
                  <h4 style={{ fontSize: '0.9rem', color: theme.primary, marginBottom: '0.2rem' }}>{step.title}</h4>
                  <p style={{ fontSize: '0.85rem', color: '#64748B' }}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    default:
      return null;
  }
};

const BACASSPBasePresentation = ({ title, slides, theme }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slide = slides[currentSlide];

  return (
    <div style={{ background: theme.background || '#FAF5FF', borderRadius: '16px', overflow: 'hidden' }}>
      <div style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`, padding: '0.8rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: 'white', fontWeight: '600', fontSize: '0.9rem' }}>{title}</span>
        <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>{currentSlide + 1} / {slides.length}</span>
      </div>
      <div style={{ padding: '1.5rem', minHeight: '380px' }}>
        <BACASSPSlideRenderer slide={slide} theme={theme} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.8rem 1.5rem', borderTop: `1px solid ${theme.primary}20` }}>
        <button onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))} disabled={currentSlide === 0} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1rem', borderRadius: '8px', border: 'none', background: currentSlide === 0 ? '#E2E8F0' : theme.primary, color: currentSlide === 0 ? '#94A3B8' : 'white', cursor: currentSlide === 0 ? 'default' : 'pointer', fontSize: '0.85rem' }}>
          <ChevronLeft size={16} /> Précédent
        </button>
        <div style={{ display: 'flex', gap: '3px' }}>
          {slides.map((_, i) => (
            <div key={i} onClick={() => setCurrentSlide(i)} style={{ width: i === currentSlide ? '16px' : '6px', height: '6px', borderRadius: '3px', background: i === currentSlide ? theme.primary : `${theme.primary}30`, cursor: 'pointer', transition: 'all 0.2s' }} />
          ))}
        </div>
        <button onClick={() => setCurrentSlide(Math.min(slides.length - 1, currentSlide + 1))} disabled={currentSlide === slides.length - 1} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1rem', borderRadius: '8px', border: 'none', background: currentSlide === slides.length - 1 ? '#E2E8F0' : theme.primary, color: currentSlide === slides.length - 1 ? '#94A3B8' : 'white', cursor: currentSlide === slides.length - 1 ? 'default' : 'pointer', fontSize: '0.85rem' }}>
          Suivant <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default BACASSPBasePresentation;
export { BACASSPSlideRenderer };
